import { memo, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import _ from 'lodash';
import { useStore } from '@/store';
import { getAssetUrl } from '@/utils/common';
import BackgroundAnimate from '@/components/BackgroundAnimate';
import { useSpring, animated } from 'react-spring';
import { useQueryPoetry } from '@/services/hooks';
import { LandmarkPoetryPlaceDetailType } from '@/store/types';
import { postionsList } from '../../dynasty';
import { driveDigitalHumanByText } from '@/pages/DigitalHuman/util/tts-manage';
import PoetryCard from '../LandmarkPoetryDetail/PoetryCard';


const LandmarkAuthorDetail = () => {
  const { data } = useQueryPoetry();
  const [list, setList] = useState<any[]>([]);
  const placeId = useStore((state) => state.landmarkPoetryPlaceId);
  const placeType = useStore((state) => state.landmarkPoetryPlaceType);
  const placeAuthor = useStore((state) => state.landmarkPoetryPlaceAuthor);
  const scrollInterval = useRef<any>(null);
  const scrollContainer = useRef<any>(null);
  const curIndexRef = useRef<number>(0);
  const place = useMemo(() => {
    return data?.find((item: any) => item.entity_id === placeId);
  }, [data, placeId]);

  const placeAuthorList = useMemo(() => {
    const authors: any = [];
    const placelists = place?.ext_neighborList.filter((item: any) => {
      if (item.vertex.label === "creative_work" && item.vertex.author) {
        const authorName = item.vertex?.author?.[0] || '';
        if (item.vertex.dynasty && authorName === placeAuthor) {
          authors.push(item.vertex);
        }
        return item;
      }
    });
    placelists?.forEach((item: any) => {
      const authorName = item.vertex?.author?.[0] || '';
      if (item.vertex.dynasty && authorName === placeAuthor) {
        authors.push({ ...item.vertex });
      }
    });
    const list = authors.map((i: any, index: number) => ({ ...i, position: postionsList[index % 8] }));
    return _.uniqBy(list, 'entityId');
  }, [place, placeAuthor]);


  // 是否应该展示
  const shouldShow = placeType === LandmarkPoetryPlaceDetailType.POEM && !!placeId && !!placeAuthor;


  // 是否展示了
  const isShow = placeType === LandmarkPoetryPlaceDetailType.POEM && !!place && !!placeAuthorList?.length;

  // 语音控制
  useEffect(() => {
    if (shouldShow && !isShow) {
      console.warn('未找到相关诗人', placeAuthor);
      if (placeAuthor && place) {
        driveDigitalHumanByText(`未找到诗人${placeAuthor}在${place.name}写的诗词，你可以选择其他诗人`);
      } else {
        driveDigitalHumanByText(`未找到诗人${placeAuthor}写的诗词，你可以选择其他诗人`);
      }
    }
    if (!isShow) return;
    driveDigitalHumanByText(`我们现在看到的是诗人${placeAuthor}在${place.name}写的诗词`);
  }, [isShow, shouldShow, place, placeAuthor])

  const contentAnimate = useSpring({
    opacity: isShow ? 1 : 0,
    scale: isShow ? 1 : 0,
    config: {
      duration: 500,
    },
  });

  useEffect(() => {
    // 轮训诗
    const randomItems: any = _.chunk(placeAuthorList || [], 8);
    curIndexRef.current = 0;
    setList(randomItems[curIndexRef.current]);
    if (randomItems?.length > 0) {
      const timer = setInterval(() => {
        if (curIndexRef.current < randomItems?.length - 1) {
          curIndexRef.current += 1;
        } else {
          curIndexRef.current = 0;
        }
        setList(randomItems[curIndexRef.current]);
      }, 10000);

      return () => clearInterval(timer);
    }
  }, [placeAuthorList])

  function startAutoScroll() {
    scrollInterval.current = setInterval(() => {
      if (!scrollContainer.current) {
        return false;
      }
      const scrollDistance = 1;
      const currentScroll = scrollContainer.current.scrollTop;
      scrollContainer.current.scrollTop = currentScroll + scrollDistance;
      if (currentScroll >= scrollContainer.current.scrollHeight - scrollContainer.current.clientHeight) {
        scrollContainer.current.scrollTop = 0;
      }
    }, 80);
  }

  useEffect(() => {
    if (scrollContainer.current) {
      startAutoScroll();
    }
    return () => {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    };
  }, [scrollContainer.current]);

  const HighlightNumbers = (text: string) => {
    const parts = text?.split(/(\d+)/).filter(Boolean) || [];
    return (
      <>
        {parts.map((part, index) => (
          // 如果是数字，则用<span>包裹并应用样式
          <span key={index} style={{
            color: part.match(/^\d+$/) ? '#9D805D' : 'inherit',
            fontSize: part.match(/^\d+$/) ? '40px' : '29px',
            fontWeight: part.match(/^\d+$/) ? '800' : '400',
            textShadow: part.match(/^\d+$/) ? `-3px -3px 0 white,
            3px -3px 0 white,
            -3px 3px 0 white,
            3px 3px 0 white,
            0 0 0 white,
            0 0 0 white,
            0 0 0 white,
            0 0 0 white` : '',

          }}>
            {part}
          </span>
        ))}
      </>
    );
  }

  return isShow && (
    <BackgroundAnimate visible={true} opacity={0} loop={true}>
      <animated.div className={styles.content} style={contentAnimate}>
        <div className={styles.introduce}>
          {HighlightNumbers(place?.biz_info?.insights_highlights)}
        </div>
        <div className={styles.mid}>
          <div className={styles.circle}>
            <div className={styles.circle_container}>
              <div className={styles.name_img}
                style={{ backgroundImage: `url(${getAssetUrl("images/landmark/nameimg.png")})` }}
              >
                <p className={styles.title}>{placeAuthor || ''}</p>
              </div>
            </div>
          </div>
          <div style={{ width: '1613px', height: '941px', position: 'relative' }}>
            {list?.map((item: any, index: number) => (
              <PoetryCard item={item} index={index} key={item.entityId} type='author' />
            ))}
            {/* {
              transitions((style, index) =>
                <animated.div style={{
                  ...style
                }}
                >
                  {list?.map((item: any, index: number) => (
                    <div
                      className={styles.content_poetry}
                      key={index}
                      style={onAngleStep(index + 1)}
                    >
                      <p className={styles.titles}>
                        <span className={styles.circle_icon}>
                          <img src={getAssetUrl('images/landmark/circleIcon.svg')} alt="" />
                        </span>
                        <span>{item?.name}</span>
                      </p>
                      <p className={styles.author}>{item.dynasty || ''}</p>
                      <div className={styles.verse}>
                        {item?.summary}
                      </div>
                    </div>
                  ))}

                </animated.div>
              )
            } */}
          </div>
        </div>
        {/* <div className={styles.left}>
          <img src={getAssetUrl("images/landmark/hangzhou.svg")} alt="" />
          <h2>{place.name}</h2>
          <div className={styles.description} ref={scrollContainer}>{place.summary}</div>
        </div> */}
      </animated.div>
    </BackgroundAnimate>
  );
};

export default memo(LandmarkAuthorDetail); 